// helpers
import { storeAnalyticsDataInLocal } from 'shared/helpers/storage/storeData';
import { AddToCartParams } from './types';
import NFA from 'shared/helpers/analytics/NFAnalytics';
import { getDynamicCategoryLevels } from './components/ProductInfo/helpers';

// types
import { IMedia } from 'shared/store/details/types';
import { EVENT_NAMES, EVENT_TYPES } from 'shared/helpers/utils/impressions';

// constants
import isEmpty from 'lodash/isEmpty';
import { DEFAULT_CURRENCY_CODE } from 'shared/constants';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { PDP_EVENTS } from 'shared/helpers/analytics/constants/pdpEvents';
import { getMergedDataWithRetinaData } from 'shared/helpers/analytics/helpers';

const getTagString = (taglist: { [key: string]: string }[] = []) => {
  if (taglist.length >= 0) return taglist.map((tag) => tag.title).join(',');

  return '';
};

const isVideoAvailableOnPDP = (medias: IMedia[] = []) => {
  let isVideoAvailable = false;

  medias.forEach((media: IMedia) => {
    if (media.mediaType === 'video') {
      isVideoAvailable = true;
    }
  });
  return isVideoAvailable;
};

const getAddToCartTrackingData = (
  productData: ProductDetail.ISizeOption | undefined,
  productType?: string,
  saleEnabled?: boolean,
  cartSpecialTag?: Record<string, any>,
  cartSale?: Record<string, any>
) => {
  if (isEmpty(productData)) return {};

  const isSpecialTagsPresentInTopN = !!productData.specialTag;
  const specialTagName = isSpecialTagsPresentInTopN ? productData.specialTag.tag : '';
  let isPriceRevealProductAvailable = false;

  if (isEmpty(productData?.price_reveal_data) === false) {
    isPriceRevealProductAvailable = true;
  } else {
    isPriceRevealProductAvailable = false;
  }

  return {
    productID: productData?.parentId || productData?.id,
    productName: productData?.subTitle,
    productBrandName: productData?.title || productData?.brandName[0],
    currency: DEFAULT_CURRENCY_CODE,
    MRP: productData?.price || productData?.mrp,
    discountedPrice: productData?.discountedPrice || productData?.finalPrice,
    discountPercent: productData?.discount,
    productTags: getTagString(productData?.tag_list),
    isVideoAvailable: isVideoAvailableOnPDP(productData?.productMedia),
    disclaimerTextShown: productData?.disclaimer || false,
    // should we show this on wishlist as we would not see this
    ...getDynamicCategoryLevels(productData?.primary_categories_json_all),
    variantType: productData?.sizeName ? productData?.sizeName : undefined,
    variantProductID: productData?.sizeName ? productData?.id : undefined,
    productColorName: productData?.color?.name,
    ...(productType ? { productType } : {}),
    ...(specialTagName ? { specialTagName } : ''),
    ...(isSpecialTagsPresentInTopN ? { isSpecialTagsPresentInTopN } : false),
    isPriceRevealProductAvailable,
    isSalePriceLive: saleEnabled || false,
    specialTag: (productData?.specialTag != null || cartSpecialTag != null) ? (productData?.specialTag?.type || cartSpecialTag?.type) : "",
    specialTagEnabled: productData?.specialTag != null || cartSpecialTag != null,
    specialTagType: (productData?.specialTag != null || cartSpecialTag != null) ? (productData?.is_sale_participating ? 'MRP/DP' : cartSale ? 'CART Rule' : "") : "",
    priceParticipatingSkuType: (productData?.is_sale_participating || cartSale) ? (productData?.is_sale_participating ? 'MRP/DP' : cartSale ? 'CART Rule' : "") : "",
    isPriceParticipatingSku: (productData?.is_sale_participating || cartSale) ? true : false,
  };
};

export const onAddToCartClick = ({
  productData,
  productType,
  saleEnabled,
  cartSpecialTag,
  cartSale,
}: AddToCartParams) => {
  // Check for 'source' URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const source = urlParams.get('source');

  const retinaData = {
    product_brand: productData?.title,
    product_dp: productData?.discountedPrice,
    product_id: productData?.id,
    product_mrp: productData?.price,
    product_price: productData?.discountedPrice,
    product_tags: productData?.tag_list
      ? productData?.tag_list.map((item) => item.title)
      : undefined,
    product_name: productData?.subTitle,
    product_currency: DEFAULT_CURRENCY_CODE,
    product_sku: productData?.sku,
    event_name: EVENT_NAMES.ADD_TO_CART,
    event_type: EVENT_TYPES.CLICK,
  };

  const data = {
    ...getAddToCartTrackingData(productData, productType, saleEnabled, cartSpecialTag, cartSale),
    source: source || 'pdp',
  };

  NFA.track({
    event: PDP_EVENTS.ADD_TO_CART,
    type: TRACK_TYPE.EVENT,
    data: getMergedDataWithRetinaData(retinaData, data),
  });
};

export const sizeSelectorLoaded = () => {
  NFA.track({
    event: PDP_EVENTS.SIZE_SELECTOR_LOADED,
    type: TRACK_TYPE.EVENT,
  });
};

export const trackPDPColorVariantClicked = (colorVariantClicked: string) => {
  storeAnalyticsDataInLocal({ PDPColorVariantClicked: colorVariantClicked });
};

export const trackBrandNameClicked = (location: string) => {
  NFA.track({
    event: PDP_EVENTS.BRAND_NAME_CLICKED,
    type: TRACK_TYPE.EVENT,
    data: {
      brandNameLocation: location,
    },
  });
};

export const trackCollapsibleSectionClicked = (sectionName: string) => {
  NFA.track({
    event: PDP_EVENTS.INFO_SECTION_CLICKED,
    type: TRACK_TYPE.EVENT,
    data: {
      expandedProductInfoSectionName: sectionName,
    },
  });
};

export const trackBestPriceShown = (isBestPriceShown: boolean, isBestPriceEligible: boolean) => {
  NFA.updateDataLayer({
    isBestPriceEligible: isBestPriceEligible,
    isBestPriceShown: isBestPriceShown,
  });
};
